import { useCallback, useEffect, useRef, useState } from "react";

// Núcleo WebRTC de voz. Handshake de "pronto": ambos os lados avisam call:ready
// quando a mídia está pronta; o "caller" (sempre o atendente) cria a oferta quando
// os dois estão prontos. Funciona nos dois sentidos (atendente→cliente e cliente→atendente).
const log = (...a) => { try { console.log("[webrtc]", ...a); } catch (e) {} };

const useWebRTCCall = ({ getSocket, ticketId, turn, role }) => {
  const [state, setState] = useState("idle"); // idle|calling|connecting|in-call|ended|failed
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const timerRef = useRef(null);
  const startedRef = useRef(false);
  const remoteSetRef = useRef(false);
  const pendingIceRef = useRef([]);
  const selfReadyRef = useRef(false);
  const otherReadyRef = useRef(false);
  const offeredRef = useRef(false);

  const iceServers = useCallback(() => {
    const servers = [{ urls: "stun:stun.l.google.com:19302" }];
    if (turn && turn.urls) {
      servers.push({ urls: turn.urls, username: turn.username, credential: turn.credential });
    }
    return servers;
  }, [turn]);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    remoteSetRef.current = false;
    pendingIceRef.current = [];
    selfReadyRef.current = false;
    otherReadyRef.current = false;
    offeredRef.current = false;
  }, []);

  const applyRemote = useCallback(async (sdp) => {
    await pcRef.current.setRemoteDescription(new RTCSessionDescription(sdp));
    remoteSetRef.current = true;
    const pending = pendingIceRef.current;
    pendingIceRef.current = [];
    for (const c of pending) {
      try { await pcRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { log("flush ice erro", e && e.message); }
    }
  }, []);

  const maybeOffer = useCallback(async () => {
    const socket = getSocket();
    if (role !== "caller" || !pcRef.current) return;
    if (!selfReadyRef.current || !otherReadyRef.current || offeredRef.current) return;
    offeredRef.current = true;
    try {
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      socket.emit("call:offer", { ticketId, sdp: pcRef.current.localDescription });
      setState("connecting");
      log("offer enviado");
    } catch (e) { log("maybeOffer erro", e && e.message); offeredRef.current = false; }
  }, [getSocket, role, ticketId]);

  const setupPeer = useCallback(async () => {
    const socket = getSocket();
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;

    const pc = new RTCPeerConnection({ iceServers: iceServers() });
    pcRef.current = pc;
    remoteSetRef.current = false;
    pendingIceRef.current = [];
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));

    pc.onicecandidate = (e) => {
      if (e.candidate) socket.emit("call:ice", { ticketId, candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      log("ontrack");
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = e.streams[0];
        remoteAudioRef.current.play().catch(() => {});
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      log("iceState", st);
      if (st === "connected" || st === "completed") {
        setState("in-call");
        if (!startedRef.current) {
          startedRef.current = true;
          timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
        }
      }
      if (st === "failed") setState("failed");
    };

    selfReadyRef.current = true;
    socket.emit("call:ready", { ticketId });
    log("pronto (ready) enviado, role=", role);
    maybeOffer();
    return pc;
  }, [getSocket, iceServers, ticketId, role, maybeOffer]);

  // Atendente inicia (caller). Opcional: ringOnStart avisa o cliente (não usado no fluxo atual).
  const startCall = useCallback(async () => {
    try { setState("calling"); await setupPeer(); }
    catch (e) { log("startCall erro", e && e.message); setState("failed"); }
  }, [setupPeer]);

  // Cliente entra na chamada (callee). ring=true avisa o painel do atendente (cliente→atendente).
  const acceptCall = useCallback(async ({ ring } = {}) => {
    try {
      setState("connecting");
      await setupPeer();
      if (ring) getSocket().emit("call:ring", { ticketId });
    } catch (e) { log("acceptCall erro", e && e.message); setState("failed"); }
  }, [setupPeer, getSocket, ticketId]);

  const hangup = useCallback(() => {
    try { getSocket().emit("call:hangup", { ticketId }); } catch (e) {}
    cleanup();
    setState("ended");
  }, [getSocket, ticketId, cleanup]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const onReady = () => { otherReadyRef.current = true; log("outro pronto"); maybeOffer(); };
    const onOffer = async (d) => {
      if (role !== "callee" || !pcRef.current) return;
      try {
        await applyRemote(d.sdp);
        const answer = await pcRef.current.createAnswer();
        await pcRef.current.setLocalDescription(answer);
        socket.emit("call:answer", { ticketId, sdp: pcRef.current.localDescription });
        log("answer enviado");
      } catch (e) { log("onOffer erro", e && e.message); }
    };
    const onAnswer = async (d) => {
      if (role !== "caller" || !pcRef.current) return;
      try { await applyRemote(d.sdp); log("answer aplicado"); } catch (e) { log("onAnswer erro", e && e.message); }
    };
    const onIce = async (d) => {
      if (!pcRef.current || !d.candidate) return;
      if (remoteSetRef.current) {
        try { await pcRef.current.addIceCandidate(new RTCIceCandidate(d.candidate)); } catch (e) { log("addIce erro", e && e.message); }
      } else {
        pendingIceRef.current.push(d.candidate);
      }
    };
    const onHangup = () => { log("hangup recebido"); cleanup(); setState("ended"); };

    socket.on("call:ready", onReady);
    socket.on("call:offer", onOffer);
    socket.on("call:answer", onAnswer);
    socket.on("call:ice", onIce);
    socket.on("call:hangup", onHangup);
    return () => {
      socket.off("call:ready", onReady);
      socket.off("call:offer", onOffer);
      socket.off("call:answer", onAnswer);
      socket.off("call:ice", onIce);
      socket.off("call:hangup", onHangup);
    };
  }, [getSocket, role, ticketId, cleanup, applyRemote, maybeOffer]);

  useEffect(() => () => cleanup(), [cleanup]);

  return { state, muted, seconds, startCall, acceptCall, hangup, toggleMute, remoteAudioRef };
};

export default useWebRTCCall;
